'use client';

import { useTranslations } from 'next-intl';
import type { CourtFeed } from '@/app/[locale]/event/[eventId]/libs/eventFeeds';
import { platformName } from '@/components/streaming/streamPlatforms';
import MatchLiveFeeds, { cameraLabel } from './MatchLiveFeeds';

export type MatchFeedsStatus = 'in_progress' | 'completed' | 'scheduled';

/**
 * The court's feeds on the futsal match page: a player under "Live now" while it is on, "Watch again"
 * once it is over. The racket page uses `MatchLiveFeeds` directly and links to Watch instead.
 */
export default function MatchFeeds({
  result,
  matchStatus,
}: {
  result: { courtId: string; dayKey: string; feeds: CourtFeed[] };
  matchStatus: MatchFeedsStatus;
}) {
  const t = useTranslations('EventWatch');
  const { feeds } = result;
  if (!feeds.length || matchStatus === 'scheduled') return null;
  const label = (feed: CourtFeed, i: number) => cameraLabel(t, feed, i);

  if (matchStatus === 'completed') {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-4 pt-4">
        <h2 className="text-foreground text-lg font-semibold">{t('watchAgain')}</h2>
        <ul className="flex flex-col gap-2">
          {feeds.map((feed, i) => (
            <li key={feed.id}>
              <a
                href={feed.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-card border-border text-foreground block rounded-lg border px-4 py-3 text-sm"
              >
                {label(feed, i)} · {platformName(feed.platform, feed.url)} ↗
              </a>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-2 px-4 pt-4">
      <h2 className="text-foreground text-lg font-semibold">{t('liveNow')}</h2>
      <MatchLiveFeeds feeds={feeds} />
    </section>
  );
}
